import React, { useState, useEffect } from 'react';
import { Form, Input, InputNumber, Switch, Card, Typography, Spin, Space, Button, Collapse, Tooltip, List, Tag, Flex, Select } from 'antd';
import { KeyOutlined, InfoCircleOutlined, ReloadOutlined, EyeOutlined, EyeInvisibleOutlined } from '@ant-design/icons';

import { TemplateDynamicFields} from '../types/index';
import { apiService } from '../services/api';
import { normalizeDynamicFieldValue, parseDynamicFieldValue } from '../utils/dynamicFields';
import { DynamicFieldsProps } from './DynamicFields.types';

const { Text } = Typography;
const { Panel } = Collapse;

export const DynamicFieldsComponent: React.FC<DynamicFieldsProps> = ({
  templateId,
  value = [],
  onChange,
  form,
  templateDynamicFields: templateDynamicFieldsProp // 🔥 ВИПРАВЛЕННЯ 1.1: Отримуємо з пропсів
}) => {
  const [loading, setLoading] = useState(false);
  const [templateDynamicFields, setTemplateDynamicFields] = useState<TemplateDynamicFields | null>(
    templateDynamicFieldsProp || null // 🔥 ВИПРАВЛЕННЯ 1.1: Ініціалізуємо з пропсів
  );
  const [visiblePasswords, setVisiblePasswords] = useState<Record<string, boolean>>({});

  // 🔥 ВИПРАВЛЕННЯ 1.1: Оновлюємо локальний стейт, коли змінюється пропс
  useEffect(() => {
    if (templateDynamicFieldsProp) {
      setTemplateDynamicFields(templateDynamicFieldsProp);
    }
  }, [templateDynamicFieldsProp]);

  useEffect(() => {
    // 🔥 ВИПРАВЛЕННЯ 1.1: Завантажуємо тільки якщо не передано через пропси
    if (templateId && !templateDynamicFieldsProp) {
      fetchTemplateFields();
    } else if (!templateId) {
      setTemplateDynamicFields(null);
    }
  }, [templateId, templateDynamicFieldsProp]);

  const fetchTemplateFields = async () => {
    if (!templateId) return;

    try {
      setLoading(true);
      const fields = await apiService.getTemplateDynamicFields(templateId);
      setTemplateDynamicFields(fields);
    } catch (error) {
      console.error('Error loading template dynamic fields:', error);
      setTemplateDynamicFields(null);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (
    fieldName: string,
    rawValue: any,
    fieldType: string = "string",
    fireOnChange = true
  ) => {
    const normalizedValue = normalizeDynamicFieldValue(fieldType, rawValue);

    // Спочатку оновлюємо форму
    if (form) {
      form.setFieldValue(["dynamicFields", fieldName], normalizedValue);
    }
    
    // Потім оновлюємо локальний стан тільки якщо onChange передано
    if (fireOnChange && onChange) {
      const currentFields = value || [];
      const updatedFields = currentFields.filter(f => f.name !== fieldName);
      
      // Перетворюємо значення в строку для збереження
      let stringValue = '';
      if (normalizedValue !== undefined && normalizedValue !== null) {
        stringValue = String(normalizedValue);
      }

      // Завжди додаємо поле якщо воно має будь-яке значення (включно з false)
      if (
        stringValue !== "" ||
        typeof normalizedValue === "boolean" ||
        typeof normalizedValue === "number"
      ) {
        updatedFields.push({ name: fieldName, value: stringValue });
      }

      onChange(updatedFields);
    }
  };

  const generateEncryptionKey = () => {
    // Генеруємо 32-символьний ключ
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < 32; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  };

  const handleGenerateKey = (fieldName: string) => {
    const generatedKey = generateEncryptionKey();
    
    // Оновлюємо тільки один раз через handleFieldChange
    handleFieldChange(fieldName, generatedKey, "encryption");
  };

  const togglePasswordVisibility = (fieldName: string) => {
    setVisiblePasswords(prev => ({
      ...prev,
      [fieldName]: !prev[fieldName]
    }));
  };

  // Функція для отримання типізованого значення
  const getTypedValue = (field: any): any => {
    // Спочатку перевіряємо форму (бо форма може містити актуальні значення з бази)
    if (form) {
      const formValue = form.getFieldValue(['dynamicFields', field.name]);
      if (formValue !== undefined && formValue !== null && formValue !== '') {
        const parsed = parseDynamicFieldValue(formValue, field.type);
        return parsed;
      }
    }
    
    const fieldNameLower = field.name.toLowerCase()
    // Якщо немає в формі, перевіряємо значення в переданому масиві value
    const localField = value?.find(f => {
      const fNameLower = f.name.toLowerCase();
      return f.name === field.name || fNameLower === fieldNameLower
    });

    if (localField?.value !== undefined && localField?.value !== null && localField.value !== '') {
      const parsed = parseDynamicFieldValue(localField.value, field.type);
      return parsed;
    }
    
    // Якщо немає ніде, то значення за замовчуванням
    const defaultValue = parseDynamicFieldValue(field.defaultValue || '', field.type);
    return defaultValue;
  };

  // Поточне значення поля (форма → пропс value → дефолт шаблону), як рядок
  const getRawFieldValue = (fieldName: string): string => {
    const formValue = form?.getFieldValue(['dynamicFields', fieldName]);
    if (formValue !== undefined && formValue !== null && formValue !== '') {
      return String(formValue);
    }
    const local = value?.find((f) => f.name.toLowerCase() === fieldName.toLowerCase());
    if (local?.value !== undefined && local?.value !== null && local.value !== '') {
      return String(local.value);
    }
    const tplField = templateDynamicFields?.dynamicFields.find(
      (f) => f.name.toLowerCase() === fieldName.toLowerCase()
    );
    return tplField?.defaultValue !== undefined ? String(tplField.defaultValue) : '';
  };

  // Умовна видимість: showIf = { field, values } — поле показується лише коли
  // залежний макрос має одне зі значень. Приховане поле не рендериться і не валідується.
  const isFieldVisible = (field: any): boolean => {
    if (!field.showIf?.field || !Array.isArray(field.showIf.values)) return true;
    const current = getRawFieldValue(field.showIf.field).trim().toLowerCase();
    return field.showIf.values.includes(current);
  };

  // Рендер компонента для конкретного типу поля
  const renderFieldInput = (field: any) => {
    const formValue = form?.getFieldValue(['dynamicFields', field.name]);
    const fieldValue =
      formValue !== undefined
        ? parseDynamicFieldValue(formValue, field.type)
        : getTypedValue(field);

    switch (field.type) {
      case 'boolean':
        return (
          <Switch
            checked={Boolean(fieldValue)}
            onChange={(value) => handleFieldChange(field.name, value, field.type)}
            checkedChildren="Так"
            unCheckedChildren="Ні"
          />
        );

      case 'number':
        return (
          <InputNumber
            style={{ width: '100%' }}
            placeholder={field.placeholder}
            value={fieldValue}
            onChange={(value) => handleFieldChange(field.name, value || 0, field.type)}
            min={0}
          />
        );

      case 'url':
        return (
          <Input
            placeholder={field.placeholder || "https://example.com"}
            value={fieldValue}
            onChange={(e) => handleFieldChange(field.name, e.target.value, field.type)}
            type="url"
          />
        );

      case 'encryption':
      case 'password':
        const isVisible = visiblePasswords[field.name];
        return (
          <Input
            placeholder={field.placeholder}
            value={fieldValue}
            onChange={(e) => handleFieldChange(field.name, e.target.value, field.type)}
            type={isVisible ? "text" : "password"}
            suffix={
              <Space size={0}>
                <Tooltip title={isVisible ? "Сховати ключ" : "Показати ключ"}>
                  <Button
                    type="text"
                    icon={isVisible ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                    onClick={() => togglePasswordVisibility(field.name)}
                    size="small"
                  />
                </Tooltip>
                <Tooltip title="Згенерувати новий ключ">
                  <Button
                    type="text"
                    icon={<ReloadOutlined />}
                    onClick={() => handleGenerateKey(field.name)}
                    size="small"
                  />
                </Tooltip>
              </Space>
            }
          />
        );

      case 'enum':
        return (
          <Select
            style={{ width: '100%' }}
            placeholder={field.placeholder || "Оберіть значення"}
            value={fieldValue}
            onChange={(value) => handleFieldChange(field.name, value, field.type)}
            options={field.options?.map((opt: string) => ({
              label: opt,
              value: opt
            })) || []}
          />
        );

      case 'string':
      default:
        return (
          <Input
            placeholder={field.placeholder}
            value={fieldValue}
            onChange={(e) => handleFieldChange(field.name, e.target.value, field.type)}
            type="text"
          />
        );
    }
  };

  if (loading) {
    return (
      <Card>
        <Flex vertical align="center" gap="middle" style={{ padding: '20px' }}>
          <Spin size="large" />
          <Text>Завантаження динамічних полів...</Text>
        </Flex>
      </Card>
    );
  }

  if (!templateId) {
    return (
      <Card>
        <Text type="secondary">Спочатку оберіть шаблон для відображення динамічних полів</Text>
      </Card>
    );
  }

  if (!templateDynamicFields) {
    return (
      <Card>
        <Text type="secondary">Не вдалося завантажити динамічні поля для обраного шаблону</Text>
      </Card>
    );
  }

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="large">
      {/* Template fields */}
      {templateDynamicFields.dynamicFields.length > 0 && (
        <Card title="📋 Поля шаблону" size="small">
          <Space direction="vertical" style={{ width: '100%' }}>
            {templateDynamicFields.dynamicFields.map((field) => {
              const renderItem = () => (
                <Form.Item
                  key={field.name}
                  label={
                    <Space>
                      {field.label}
                      {field.description && (
                        <Tooltip title={field.description}>
                          <InfoCircleOutlined style={{ color: '#1890ff', fontSize: '12px' }} />
                        </Tooltip>
                      )}
                      {(field.type === 'encryption' || field.type === 'password') && <KeyOutlined style={{ color: '#52c41a' }} />}
                    </Space>
                  }
                  name={['dynamicFields', field.name]}
                  rules={[
                    ...(field.required ? [{ required: true, message: `${field.label} є обов'язковим` }] : []),
                    ...(field.type === 'url' ? [{ 
                      pattern: /^https?:\/\/.+\..+/,
                      message: 'Введіть правильний URL (наприклад: https://example.com)' 
                    }] : []),
                    ...(field.type === 'enum' && field.options && field.options.length > 0 ? [{
                      validator: (_: any, value: string) => {
                        if (!value) {
                          return Promise.resolve();
                        }
                        const options = field.options;
                        if (!options || !options.includes(value)) {
                          return Promise.reject(new Error(`Значення має бути одним з: ${options?.join(', ') || ''}`));
                        }
                        return Promise.resolve();
                      }
                    }] : [])
                  ]}
                  initialValue={getTypedValue(field)}
                  valuePropName={field.type === 'boolean' ? 'checked' : 'value'}
                >
                  {renderFieldInput(field)}
                </Form.Item>
              );

              if (!field.showIf?.field) {
                return renderItem();
              }

              // Перерендер лише при зміні залежного поля; приховане поле не монтується,
              // тому його rules (required) не блокують сабміт.
              const depName = field.showIf.field;
              return (
                <Form.Item
                  key={`${field.name}__showIf`}
                  noStyle
                  shouldUpdate={(prev: any, cur: any) =>
                    prev?.dynamicFields?.[depName] !== cur?.dynamicFields?.[depName]
                  }
                >
                  {() => (isFieldVisible(field) ? renderItem() : null)}
                </Form.Item>
              );
            })}
          </Space>
        </Card>
      )}

      {/* Template info */}
      <Collapse ghost>
        <Panel header="ℹ️ Інформація про шаблон" key="template-info">
          <Card size="small">
            <Space direction="vertical" style={{ width: '100%' }}>
              <Text>
                <Text strong>Назва:</Text> {templateDynamicFields.template.name}
              </Text>
              <Text>
                <Text strong>Категорія:</Text> {templateDynamicFields.template.category}
              </Text>
              
              {templateDynamicFields.exports.length > 0 && (
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text strong>Експортні функції:</Text>
                  <List
                    size="small"
                    dataSource={templateDynamicFields.exports}
                    renderItem={(exp) => (
                      <List.Item>
                        <Tag>{exp.name}</Tag>
                        {exp.description && <Text type="secondary"> - {exp.description}</Text>}
                      </List.Item>
                    )}
                  />
                </Space>
              )}

              {templateDynamicFields.endpoints.length > 0 && (
                <Space direction="vertical" size="small" style={{ width: '100%' }}>
                  <Text strong>Доступні ендпоінти:</Text>
                  <List
                    size="small"
                    dataSource={templateDynamicFields.endpoints}
                    renderItem={(endpoint) => (
                      <List.Item>
                        <Text code>{endpoint.method} /{endpoint.path}</Text>
                        <Text> → {endpoint.name}</Text>
                        {endpoint.description && <Text type="secondary"> - {endpoint.description}</Text>}
                      </List.Item>
                    )}
                  />
                </Space>
              )}
            </Space>
          </Card>
        </Panel>
      </Collapse>
    </Space>
  );
}; 